import uniqBy from 'lodash-es/uniqBy';
import cloneDeep from 'lodash-es/cloneDeep';
import uniq from 'lodash-es/uniq';

import { mapLayout, createCorrectnessTag } from './utils.js';
import {
  createSessionValueGroupingTag,
  parseKey,
  createDefaultHeaders,
} from './classifierUtils.js';

/**
 * itemsWithSessionsData structure:
 * {
    ITEM_ID: {
        id: Parsed_ITEM_ID,
        config: {...},
        headers: [
            {
                data: [...],
                autoScore: {...},
                correctnessGroup: "correct" | "correct-partially" | "incorrect" | "unanswered",
                sessionValueSubGroup: Session_Value_Grouping_Tag
            }
        ],
        classroomToolLayout: "row" | "column",
        sessions: {
            SESSION_ID: {
                id: SESSION_ID,
                contentItemId: ITEM_ID,
                data: [...],
                autoScore: {...},
                manualScore: null,
                createdAt: ...,
                studentId: STUDENT_ID,
                student: {...},
                correctnessGroup: "correct" | "correct-partially" | "incorrect" | "unanswered",
                sessionValueSubGroup: Session_Value_Grouping_Tag
            },
        },
        noStudentSessionIds: [SESSION_ID],
        unsortedSessions: [...]
    }
}
 * where:
 * headers: default (or composed) headers for each group and subgroup
 * sessions: all sessions that belong to that item, having:
 *      correctnessGroup: to determine to what group they belong
 *      sessionValueSubGroup: to determine to what subgroup they belong (for most of the items, it's "default", but for some, it's created using session's data value)
 * unsortedSessions: all sessions that were not sorted (mostly used when we get sessions before item)
 * noStudentSessionIds: all sessions that have no student assigned (mostly used when we get sessions before students)
 */
export class Classifier {
  constructor() {
    this.students = [];
    this.itemsWithSessionsData = {};

    this.findStudent = this.findStudent.bind(this);
    this.appendNewStudents = this.appendNewStudents.bind(this);
    this.appendNewSession = this.appendNewSession.bind(this);
    this.appendNewItem = this.appendNewItem.bind(this);
    this.setStudentsOnItemSessions = this.setStudentsOnItemSessions.bind(this);
    this.addFakeSessionsForTheStudentsWithoutSessions = this.addFakeSessionsForTheStudentsWithoutSessions.bind(
      this
    );

    this.emitItemData = this.emitItemData.bind(this);
  }

  findStudent(studentId) {
    const student =
      this.students && this.students.find(stud => stud.id === studentId);

    if (student) {
      // construct "name" using first and last name
      student.name = `${student.firstName} ${student.lastName}`;
    }

    return student;
  }

  setStudentsOnItemSessions(itemId) {
    // this function is called when sessions are loaded before students
    const item = this.itemsWithSessionsData[itemId];
    const { sessions, noStudentSessionIds } = item || {};

    // if one session doesn't have a student set, make sure to set one (if student is available)
    if (sessions && noStudentSessionIds && noStudentSessionIds.length) {
      const newNoStudentSessionIds = [];

      noStudentSessionIds.forEach(sessionId => {
        const session = item.sessions[sessionId];

        // if there isn't a student set for this session
        if (!session.student) {
          const student = this.findStudent(session.studentId);

          // if we found the student, we set it, otherwise, we re-add it in noStudentSessionIds
          if (student) {
            session.student = student;
          } else {
            newNoStudentSessionIds.push(sessionId);
          }
        }
      });

      item.noStudentSessionIds = newNoStudentSessionIds;
    }
  }

  appendNewStudents(students) {
    // first, append new students and make sure all are unique
    this.students = uniqBy([...students, ...this.students], stud => stud.id);

    const allItemIds = Object.keys(this.itemsWithSessionsData || {});

    // after that, check all the sessions that don't have a student set;
    allItemIds.forEach(this.setStudentsOnItemSessions);
  }

  appendNewSession(session) {
    const { studentId, contentItemId, autoScore, manualScore, id } =
      session || {};

    const itemId = parseKey(contentItemId);

    // if item was not created yet, create it
    if (!this.itemsWithSessionsData[itemId]) {
      this.itemsWithSessionsData[itemId] = {
        unsortedSessions: [],
      };
    }

    const item = this.itemsWithSessionsData[itemId];

    // if item was not loaded (doesn't have config), push session to unsortedSessions
    if (!item.config) {
      item.unsortedSessions.push(session);

      return;
    }

    const { points } = autoScore || manualScore || {};

    const correctnessTag = createCorrectnessTag(points);
    const sessionTag = createSessionValueGroupingTag(item, session);
    const sessionValueGroupingTag = parseKey(sessionTag);

    const student = this.findStudent(studentId);

    // if student was not found, push the session in noStudentSessionIds
    if (!student) {
      if (!this.itemsWithSessionsData[itemId].noStudentSessionIds) {
        this.itemsWithSessionsData[itemId].noStudentSessionIds = [];
      }

      this.itemsWithSessionsData[itemId].noStudentSessionIds.push(session.id);
      this.itemsWithSessionsData[itemId].noStudentSessionIds = uniq(
        this.itemsWithSessionsData[itemId].noStudentSessionIds
      );
    }

    // add session in current item's sessions
    this.itemsWithSessionsData[itemId].sessions = {
      ...this.itemsWithSessionsData[itemId].sessions,
      [id]: {
        ...session,
        student,
        correctnessGroup: correctnessTag,
        sessionValueSubGroup: sessionValueGroupingTag,
      },
    };
  }

  appendNewItem(item) {
    const { id } = item || {};
    const itemId = parseKey(id);

    const defaultHeaders = createDefaultHeaders(item);

    this.itemsWithSessionsData[itemId] = {
      ...this.itemsWithSessionsData[itemId],
      ...item,
      headers: defaultHeaders.headers,
      classroomToolLayout: mapLayout(item),
    };

    // check if there are sessions unsorted and sort them (if sessions were loaded before item)
    if (this.itemsWithSessionsData[itemId].unsortedSessions) {
      this.itemsWithSessionsData[itemId].unsortedSessions.forEach(
        this.appendNewSession
      );
      this.itemsWithSessionsData[itemId].unsortedSessions = [];
    }
  }

  appendData(data) {
    const { items, sessions, students, emitDataItemId } = data || {};

    if (students) {
      this.appendNewStudents(students);
    }

    if (items) {
      items.forEach(this.appendNewItem);
    }

    if (sessions) {
      sessions.forEach(this.appendNewSession);
    }

    if (emitDataItemId) {
      this.emitItemData(emitDataItemId);
    }
  }

  addFakeSessionsForTheStudentsWithoutSessions(id) {
    const itemId = parseKey(id);
    const data = this.itemsWithSessionsData[itemId] || {};

    data.sessions = data.sessions || {};

    // get the students that are already used and make sure we don't add fake sessions for them
    const usedStudents = uniqBy(
      Object.values(data.sessions).map(session => session.studentId)
    );
    const dataClone = cloneDeep(data);

    if (this.students) {
      this.students.forEach(student => {
        // if student was not used, add a fake session for him, to be displayed in "No Answer column"
        if (usedStudents.indexOf(student.id) < 0) {
          dataClone.sessions[`fake_session_for_student_${student.id}`] = {
            id: `fake_session_for_student_${student.id}`,
            studentId: student.id,
            student: {
              ...student,
              name: `${student.firstName} ${student.lastName}`,
            },
            correctnessGroup: 'unanswered',
            sessionValueSubGroup: 'default',
          };
        }
      });
    }

    return dataClone;
  }

  emitItemData(id) {
    const data = this.addFakeSessionsForTheStudentsWithoutSessions(id);

    window.dispatchEvent(
      new CustomEvent('classroom-tool-teacher-live-new-data', {
        detail: { itemWithSessionsData: data },
      })
    );
  }
}

export default Classifier;
